import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { of, Subject, take, tap, combineLatest, mergeAll, map, mergeMap, toArray, catchError } from 'rxjs';
import { Router } from '@angular/router';
import { ICtaOutput, IOptionOutput, ITableColumns, ITableConfigParams, ITableConfigParamsFilter, ITableData, TableComponent } from '../../../shared/components/table/table.component';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from '../../../core/services/user.service';
import { TranslateService } from '@ngx-translate/core';
import { FacilitiesService } from '../../../core/services/facilities.service';
import { CustomerService } from '../../../core/services/customer.service';
import { RoleToTranslation } from '../../../shared/enums/userRole.enum';
import { ToastService } from '../../../core/services/utils/toast.service';
import { IonicColors } from '../../../shared/enums/ionicColors.enum';
import { RequestsService } from '../../../core/services/utils/requests.service';
import { FiltersList } from '../../../shared/components/table/filtersList';
import { IonContent } from "@ionic/angular/standalone";

@Component({
  selector: 'app-users',
  templateUrl: 'users.page.html',
  styleUrls: ['users.page.scss'],
  imports: [IonContent, CommonModule, TableComponent, MatCardModule]
})
export class UsersPage {
  public tableColumns: ITableColumns[] = [
    { label: 'USER', id: 'user', showCol: true, cannotHide: true },
    { label: 'ROLE', id: 'role', showCol: true },
    { label: 'CUSTOMER', id: 'customer', showCol: true },
    { label: 'ORGANIZATION', id: 'organization', showCol: true },
    { label: 'EMAIL', id: 'email', showCol: true },
    { label: 'LANGUAGE', id: 'language', showCol: true },
  ];

  public tableData: ITableData[] = [];

  private translateService = inject(TranslateService);
  public tableConfigParams: ITableConfigParams = {
    id: 'users',
    referenceLabel: 'USERS',
    contactInfo: {
      title: 'user',
      subtitles: [{ value: 'creation_date' }],
    },
    filters: [
      this.filtersList.getFilterById('role')
    ].filter((filter) => Boolean(filter)) as ITableConfigParamsFilter[],
    searchBy: ['user']
  };
  private router = inject(Router)
  public isDataLoading: boolean = false;

  constructor(
    public dialog: MatDialog,
    private userService: UserService,
    private alertController: AlertController,
    private facilitiesService: FacilitiesService,
    private customerService: CustomerService,
    private toastService: ToastService,
    private requestsService: RequestsService,
    private cdRef: ChangeDetectorRef,
    private filtersList: FiltersList
  ) {
  }

  private destroyed$ = new Subject();

  ngOnInit() {
    this.requestsService.getCurrentUser()?.role === 'superuser' && this.tableColumns.unshift({ label: 'ID', id: 'id', showCol: true })
  }

  ngAfterViewInit() {
    this.loadUsers();
  }

  ngAfterViewChecked() {
    this.cdRef.detectChanges();
  }

  loadUsers(showToast: boolean = false) {
    this.isDataLoading = true;
    combineLatest([
      this.userService.loadUsers$(),
      this.customerService.loadCustomers$().pipe(
        take(1),
        mergeAll(),
        mergeMap((customer) =>
          this.facilitiesService.getOrganization$(customer.id).pipe(
            map((organizations) => ({
              id: customer.id,
              name: customer.businessName,
              orgs: organizations.map((organization) => ({
                id: organization.id,
                name: organization.name
              }))
            })),
            catchError((err) => {
              return of(null);
            })
          ))
        ,
        toArray()
      )
    ]).pipe(
      take(1),
      tap(([users, customersAndOrgs]) => {
        this.tableData = users.users.map((item) => {
          return {
            data: {
              id: item.id,
              user: `${item.name} ${item.lastname}`,
              role: this.translateService.instant('PAGES.USERS.ROLES.' + RoleToTranslation[item.role]),
              email: item.email,
              language: this.translateService.instant('GENERAL.LANGUAGES.' + item.language.toUpperCase()),
              building: null,
              customer: customersAndOrgs.find((customer) => customer && (customer.id === item.customerId))?.name || "-",
              organization: (customersAndOrgs.find((customer) => customer && (customer.id === item.customerId))?.orgs || []).find((org) => org.id === item.organizationId)?.name || "-",
            },
            options: [
              {
                action: 'edit',
                icon: 'edit',
                label: 'EDIT',
              },
              {
                action: 'delete',
                icon: 'delete',
                label: 'DELETE',
              },
            ].filter((x) => x),
          } as ITableData;
        });
        showToast && this.toastService.showToast(this.translateService.instant('GENERAL.SUCCESS.DATA_REFRESHED'), IonicColors.GREEN);
        this.isDataLoading = false;
      }),
      catchError(err => {
        this.isDataLoading = false;
        showToast && this.toastService.showToast(this.translateService.instant('GENERAL.ERRORS.LOADING_DATA'), IonicColors.RED);
        throw err;
      })
    ).subscribe();
  }

  /**
   * @description Funzione invocata al click di un button (Aggiungi risorsa, refresh dati, ecc...)
   * @param event Viene passato un oggetto contenente il tipo di action invocata
   */
  onCtaAction(event: ICtaOutput) {
    switch (event.action) {
      case 'add':
        this.router.navigate(['/wita/users/new']);
        break;
      case 'refresh':
        this.loadUsers(true);
        break;
      default:
        break;
    }
  }

  /**
   * @description Funzione invocata al click di una opzione della table
   * @param event Viene passato un oggetto contenente l'id delle risorsa in oggetto e l'action invocata
   */
  async onOptionAction(event: IOptionOutput) {
    switch (event.action) {
      case 'edit':
        this.router.navigate([`/wita/users/edit/${event.resource.id}`]);
        break;
      case 'delete':
        const customerAlert = await this.alertController.create({
          mode: 'md',
          cssClass: 'soft-alert-dialog',
          header: this.translateService.instant('GENERAL.MESSAGES.CAUTION'),
          message: 'Se veramente sicuro?',//this.translateService.instant('GENERAL.MESSAGES.DELETE_CUSTOMER_MESSAGE'),
          buttons: [
            {
              text: this.translateService.instant('GENERAL.BUTTONS.OK'),
              cssClass: 'alert-btn-danger',
              handler: () => {
                customerAlert.dismiss();
                this.userService.deleteUser$(event.resource.id).subscribe(() => {
                  this.loadUsers();
                });
              },
            },
            {
              text: this.translateService.instant('GENERAL.BUTTONS.CANCEL'),
              cssClass: 'alert-btn-neutral',
              handler: () => {
                customerAlert.dismiss();
              },
            },
          ],
        });
        await customerAlert.present();
        break;
      default:
        this.router.navigate(['/wita/users']);
        break;
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(1)
    this.destroyed$.complete()
  }

}
